import './react-phone-number-input.css';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { Autocomplete } from '@mui/material';
import { styled } from '@mui/material/styles';
import Rating from '@mui/material/Rating';
import Iconify from 'src/components/Iconify';
import { formataNumeroCelular, formatMoedaBR, maskMoedaBR, parseMoedaBR } from 'src/utils/functions';
import NovoContatoForm from './NovoContatoForm';
import v2_actionPesquisarContatos from 'src/actions/v2/contatos/v2_actionPesquisarContatos';
import InputStyleAutocomplete from 'src/components/InputStyleAutocomplete';
import { setModalAlerta } from 'src/redux/slices/global';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';

// ----------------------------------------------------------------------

const StyledRating = styled(Rating)({
    width: 150,
    '& .MuiRating-iconFilled': { color: '#ff6d75' },
    '& .MuiRating-iconHover': { color: '#ff3d47' },
});

const labels = {
    1: 'Sem interesse',
    2: 'Frio',
    3: 'Morno',
    4: 'Esquentando',
    5: 'Quente',
};

function getLabelText(value) {
    return `${value} ${value !== 1 ? 's' : ''}, ${labels[value]}`;
}

function NovoNegocioForm({ user, cliente, configCrm, loadingNovoContato, callbackAddLead, onSelectPipeline, dispatch, hideActionButtons }, ref) {
    const [contactId, setContactId] = useState(null);
    const [hover, setHover] = useState(-1);
    const [listaContatos, setListaContatos] = useState([]);
    const [pesquisa, setPesquisa] = useState('');

    const [negocio, setNegocio] = useState({
        nome: null,
        pipelineId: null,
        pipelineStageId: null,
        origemLead: null,
        qualificacao: 1,
        valor: null,
        comment: '',
        empresaId: null,
        contatoId: null,
    });

    const handleCallbackAddLead = () => {
        const { nome, pipelineId, pipelineStageId } = negocio;
        if (!nome || !pipelineId || !pipelineStageId) {
            return dispatch(setModalAlerta({ open: true, titulo: "Atenção", mensagem: 'Preencha todos os campos obrigatórios' }));
        }
        callbackAddLead({ ...negocio, valor: parseMoedaBR(negocio.valor) });
        clearFields();
    };

    useImperativeHandle(ref, () => ({
        submit: handleCallbackAddLead
    }));

    function clearFields() {
        setNegocio({
            nome: null, pipelineId: null, pipelineStageId: null,
            origemLead: null, qualificacao: 1, valor: null, comment: '',
            contatoId: null, empresaId: null,
        });
        setContactId(null);
        setPesquisa('');
    }

    async function pesquisarContatos(termo) {
        try {
            const result = await v2_actionPesquisarContatos(cliente._id, termo);
            setListaContatos(result.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    return (
        <div className="flex flex-col space-y-5">
            {/* Nome do lead */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                    Nome do lead <span className="text-red-500">*</span>
                </Label>
                <Input
                    placeholder="Ex: Negócio importante"
                    onChange={e => setNegocio({ ...negocio, nome: e.target.value })}
                    value={negocio?.nome || ''}
                    className="h-10 bg-background"
                />
            </div>

            {/* Funil */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                    Funil <span className="text-red-500">*</span>
                </Label>
                <Select
                    value={negocio?.pipelineId || ''}
                    onValueChange={async (value) => {
                        setNegocio({ ...negocio, pipelineId: value, pipelineStageId: null });
                        await onSelectPipeline(value);
                    }}
                >
                    <SelectTrigger className="w-full h-10 bg-background border-input">
                        <SelectValue placeholder="Selecione um funil" />
                    </SelectTrigger>
                    <SelectContent>
                        {configCrm?.pipelines?.map((option) => (
                            <SelectItem key={option._id} value={option._id}>
                                {option.nome}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Estágio do Funil */}
            {negocio?.pipelineId && (
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                        Estágio do Funil <span className="text-red-500">*</span>
                    </Label>
                    <Select
                        value={negocio?.pipelineStageId || ''}
                        onValueChange={(value) => setNegocio({ ...negocio, pipelineStageId: value })}
                    >
                        <SelectTrigger className="w-full h-10 bg-background border-input">
                            <SelectValue placeholder="Selecione um estágio" />
                        </SelectTrigger>
                        <SelectContent>
                            {configCrm?.stages?.map((option) => (
                                <SelectItem key={option._id} value={option._id}>
                                    {option.nome}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Origem */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Origem do contato</Label>
                <Select
                    value={negocio?.origemLead || ''}
                    onValueChange={(value) => setNegocio({ ...negocio, origemLead: value })}
                >
                    <SelectTrigger className="w-full h-10 bg-background border-input">
                        <SelectValue placeholder="Selecione uma origem" />
                    </SelectTrigger>
                    <SelectContent>
                        {configCrm?.origens?.map((option) => (
                            <SelectItem key={option._id} value={option._id}>
                                {option.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Qualificação */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Qualificação</Label>
                <div className="flex items-center gap-3">
                    <StyledRating
                        onChange={(event) => setNegocio({ ...negocio, qualificacao: parseInt(event.target.value) })}
                        icon={<Iconify width={22} height={22} icon="fluent:fire-24-filled" />}
                        emptyIcon={<Iconify width={22} height={22} icon="fluent:fire-24-regular" />}
                        name="qualificacao"
                        getLabelText={getLabelText}
                        onChangeActive={(event, newHover) => setHover(newHover)}
                        size="large"
                        defaultValue={1}
                        value={negocio.qualificacao}
                    />
                    <span className="text-xs text-muted-foreground">
                        {labels[hover !== -1 ? hover : negocio.qualificacao]}
                    </span>
                </div>
            </div>

            {/* Valor + Comentário */}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Valor do negócio</Label>
                    <Input
                        placeholder="0,00"
                        inputMode="decimal"
                        onChange={e => setNegocio({ ...negocio, valor: maskMoedaBR(e.target.value) })}
                        onBlur={() => {
                            if (negocio?.valor) {
                                setNegocio((prev) => ({ ...prev, valor: formatMoedaBR(prev.valor) }));
                            }
                        }}
                        value={negocio?.valor || ''}
                        className="h-10 bg-background"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Comentário</Label>
                    <Input
                        placeholder="Observações..."
                        onChange={e => setNegocio({ ...negocio, comment: e.target.value })}
                        value={negocio?.comment || ''}
                        className="h-10 bg-background"
                    />
                </div>
            </div>

            <Separator className="bg-border/60" />

            {/* Contato */}
            <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Contato</Label>
                <Autocomplete
                    filterOptions={(x) => x}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    options={listaContatos}
                    value={contactId}
                    inputValue={pesquisa}
                    noOptionsText="Pesquise por nome ou telefone..."
                    onChange={(event, newValue) => {
                        setNegocio({ ...negocio, contatoId: newValue?._id });
                        setContactId(newValue);
                    }}
                    onInputChange={async (event, newInputValue) => {
                        setPesquisa(newInputValue);
                        if (newInputValue.length > 3) {
                            pesquisarContatos(newInputValue);
                        } else {
                            setListaContatos([]);
                        }
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : (option.nome + ' - ' + formataNumeroCelular(option.telefone))
                    }
                    renderInput={(params) => <InputStyleAutocomplete {...params} />}
                    size="small"
                />
            </div>

            {!contactId && (
                <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="novo-contato" className="border rounded-lg border-violet-200 bg-violet-50/30">
                        <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline px-4 py-3">
                            <div className="flex items-center gap-2">
                                <Iconify icon="mdi:account-plus-outline" className="w-4 h-4 text-violet-600" />
                                Adicionar contato
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                            <NovoContatoForm
                                cliente={cliente}
                                hideSelectEmpresa={true}
                                showSaveButton={true}
                                callbackAddContact={async (data) => {
                                    // callback placeholder
                                }}
                            />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}

            {/* Botão de ação */}
            {!hideActionButtons && (
                <div className="pt-2">
                    <ButtonLoading
                        loading={loadingNovoContato}
                        onClick={handleCallbackAddLead}
                        className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white rounded-md flex items-center justify-center gap-2 transition-all font-medium shadow-sm shadow-violet-500/20 hover:shadow-violet-500/40"
                    >
                        <Iconify icon="mdi:plus" className="w-[18px] h-[18px]" />
                        Criar Lead
                    </ButtonLoading>
                </div>
            )}
        </div>
    );
}

export default forwardRef(NovoNegocioForm);
